import React, { useMemo, useState } from 'react';
import { usePos } from '../context/PosContext';
import { formatDate } from '../services/datetime';
import { printReportAuto } from '../services/receipt';
import './Sections.css';

// Product Sale Report hub (product_sale_menu_layout.xml): a date-range selector
// plus buttons that branch to Product-Wise and Category-Wise sale reports. The
// aggregations mirror the matching cards on the main Reports dashboard.
type RangeKey = 'today' | 'yesterday' | 'month' | 'custom';
type ReportKey = 'product' | 'category';

interface Result {
  title: string;
  columns: string[];
  rows: (string | number)[][];
}

const ProductSaleReport: React.FC = () => {
  const { sales, products, formatMoney } = usePos();
  const today = new Date().toISOString().split('T')[0];
  const [range, setRange] = useState<RangeKey>('today');
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [result, setResult] = useState<Result | null>(null);

  // Resolve the active [from, to] window from the selected range.
  const period = useMemo(() => {
    const now = new Date();
    const iso = (d: Date) => d.toISOString().split('T')[0];
    if (range === 'today') return { from: today, to: today };
    if (range === 'yesterday') {
      const y = iso(new Date(Date.now() - 86400000));
      return { from: y, to: y };
    }
    if (range === 'month') {
      return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: today };
    }
    return { from: start, to: end };
  }, [range, start, end, today]);

  const completed = useMemo(() => {
    const lo = new Date(period.from).getTime();
    const hi = new Date(period.to + 'T23:59:59').getTime();
    return sales.filter((s) => {
      const t = new Date(s.timestamp).getTime();
      return s.status === 'completed' && t >= lo && t <= hi;
    });
  }, [sales, period]);

  const open = (key: ReportKey) => {
    const m = (n: number) => formatMoney(n);
    if (key === 'product') {
      const map = new Map<string, { name: string; qty: number; rev: number }>();
      completed.forEach((s) =>
        s.items.forEach((i) => {
          const r = map.get(i.productId) ?? { name: i.name, qty: 0, rev: 0 };
          r.qty += i.quantity;
          r.rev += i.total;
          map.set(i.productId, r);
        })
      );
      setResult({
        title: 'Product Wise Sale',
        columns: ['Product', 'Qty', 'Revenue'],
        rows: Array.from(map.values())
          .sort((a, b) => b.rev - a.rev)
          .map((r) => [r.name, r.qty, m(r.rev)]),
      });
    } else {
      const cat = (pid: string) => products.find((p) => p.id === pid)?.category || 'Uncategorized';
      const map = new Map<string, { qty: number; rev: number }>();
      completed.forEach((s) =>
        s.items.forEach((i) => {
          const k = cat(i.productId);
          const r = map.get(k) ?? { qty: 0, rev: 0 };
          r.qty += i.quantity;
          r.rev += i.total;
          map.set(k, r);
        })
      );
      setResult({
        title: 'Category Wise Sale Report',
        columns: ['Category', 'Qty', 'Revenue'],
        rows: Array.from(map.entries())
          .sort((a, b) => b[1].rev - a[1].rev)
          .map(([k, v]) => [k, v.qty, m(v.rev)]),
      });
    }
  };

  const exportCsv = () => {
    if (!result) return;
    const csv = [result.columns, ...result.rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${result.title.replace(/\s+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printUsb = () => {
    if (!result) return;
    printReportAuto(result.title, `${formatDate(period.from)} -> ${formatDate(period.to)}`, result.columns, result.rows);
  };

  return (
    <div className="section-page">
      <div className="page-header">
        <h1>Product Sale Report</h1>
        <select value={range} onChange={(e) => setRange(e.target.value as RangeKey)}>
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="month">This Month</option>
          <option value="custom">Date Custom</option>
        </select>
      </div>

      {range === 'custom' && (
        <div className="field-row">
          <div className="field">
            <label>Start</label>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div className="field">
            <label>End</label>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
        </div>
      )}

      <div className="section-card">
        <p className="muted" style={{ marginBottom: 12 }}>
          {formatDate(period.from)} → {formatDate(period.to)} · {completed.length} bill
          {completed.length === 1 ? '' : 's'}
        </p>
        <button className="btn-teal psm-btn" onClick={() => open('product')}>
          Product Wise Sale
        </button>
        <button className="btn-teal psm-btn" onClick={() => open('category')}>
          Category Wise Sale Report
        </button>
      </div>

      {result && (
        <div className="modal-overlay" onClick={() => setResult(null)}>
          <div className="section-card" style={{ minWidth: 440, maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>{result.title}</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-grey" onClick={() => window.print()}>
                  Print
                </button>
                <button className="btn-grey" onClick={printUsb}>
                  🖨 USB Print
                </button>
                <button className="btn-grey" onClick={exportCsv}>
                  CSV
                </button>
                <button className="btn-teal" onClick={() => setResult(null)}>
                  Close
                </button>
              </div>
            </div>
            <table className="section-table" style={{ marginTop: 10 }}>
              <thead>
                <tr>
                  {result.columns.map((c, i) => (
                    <th key={i} className={i === result.columns.length - 1 ? 'right' : ''}>
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.rows.length === 0 ? (
                  <tr>
                    <td colSpan={result.columns.length} className="muted">
                      No sales in range.
                    </td>
                  </tr>
                ) : (
                  result.rows.map((row, ri) => (
                    <tr key={ri}>
                      {row.map((cell, ci) => (
                        <td key={ci} className={ci === row.length - 1 ? 'right' : ''}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductSaleReport;
